import { ReactNode, useCallback, useRef } from 'react';

import { cn } from '@/lib/utils';

import { useDragScroll } from '../model/use-drag-scroll';
import type { HorizontalScroll } from '../model/use-horizontal-scroll';
import { useStickyRows } from '../model/use-sticky-rows';

/**
 * Scroll container of the data table.
 *
 * Its horizontal state — the `data-scroll-start` / `data-scroll-end` edge flags that pinned cells and the
 * grab cursor read, and the scroll arrows — comes from `useHorizontalScroll`, owned by DataTable because
 * the arrows live outside this component (toolbar, fullscreen header). Mind that hook's note on keeping
 * the first child's DOM node stable.
 *
 * Also pannable by pointer drag (`useDragScroll`), the way a mouse-without-trackpad user reaches columns
 * hidden behind the pinned actions cell without aiming at the arrows.
 *
 * Stacks expanded hierarchy rows under the sticky header (`useStickyRows`).
 */
export function TableScrollArea({
  scroll,
  className,
  children
}: {
  scroll: Pick<HorizontalScroll, 'ref' | 'id'>;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useDragScroll(ref);
  useStickyRows(ref);

  const scrollRef = scroll.ref;
  // One node, two owners: drag and sticky rows read it through `ref`, useHorizontalScroll wires itself in its callback
  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node;
      const cleanup = scrollRef(node);
      // Returning a cleanup means React 19 never calls this with null — let go of the node here
      return () => {
        ref.current = null;
        cleanup?.();
      };
    },
    [scrollRef]
  );

  return (
    <div
      ref={setRef}
      id={scroll.id}
      className={cn(
        // Offer the grab handle exactly while there is something to pan to:
        // one of the two edge flags is set precisely when content overflows
        // horizontally. Buttons keep their own `cursor-pointer`.
        'data-[scroll-start]:cursor-grab data-[scroll-end]:cursor-grab',
        'data-[dragging]:cursor-grabbing data-[dragging]:select-none',
        className
      )}>
      {children}
    </div>
  );
}
